import React, { useCallback, useState } from 'react';
import { CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';
import * as yup from 'yup';
import { FORM_ERROR } from 'final-form';
import { useHistory } from 'react-router-dom';
import Button, { Themes as ButtonThemes } from 'ui/Button/Button';
import Input from 'ui/Input/Input';
import api from 'api/api';
import { AxiosStatus } from 'api/axiosConfig';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { validateEmail } from 'utils/validation';
import Form from 'ui/Form/Form';
import { FormType } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import { IApiResponse } from 'utils/models';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import { NETWORK_REQUIRED } from 'utils/constants';
import routePaths from 'routes/routePaths';
import { OTPVerificationModal, StatusModal } from 'components/modals';
import { IModalProps } from '../IModalProps';
import ModalWindow from '../ModalWindow/ModalWindow';
import { IEmailStatementBody, IEmailStatementFields } from './EmailStatementModal.model';
import styles from './EmailStatementModal.module.css';

type Props = {
  activeAccountNumber: string;
  startDate: string;
  endDate: string;
};

const schema = yup.object().shape({ email: validateEmail() });

const EmailStatementModal = ({ open, onClose, activeAccountNumber, startDate, endDate }: IModalProps & Props) => {
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [OTPModalError, setOTPModalError] = useState<IOTPError | null>(null);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const [isOTPModalOpen, setOTPModalOpen] = useState(false);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  const history = useHistory();

  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };
  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
    setSuccessMsg('');
    setErrorMsg('');
  };

  const closeOTPModal = () => {
    setOTPModalOpen(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const handleEmailSubmit = useCallback(async (values: IEmailStatementFields) => {
    setSubmitStatus(AxiosStatus.PENDING);
    setEmail(values.email);
    await api.post('/customer/otp/generate');
    setOTPModalOpen(true);
  }, []);

  const onResend = () => api.post('/customer/otp/generate');

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const reset = useCallback(() => {
    setSubmitStatus(AxiosStatus.DEFAULT);
    setEmail('');
  }, []);

  const closeModal = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const validate = useCallback((values) => {
    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      const valErrs = mapYupObjectError(e);
      return objectMap(valErrs, (v) => v[0]);
    }
    return undefined;
  }, []);

  const doSubmit = useCallback(
    (otp: string | undefined, form: FormType | undefined) => {
      const body: IEmailStatementBody = {
        email,
        otp,
        startDate,
        endDate,
        isDownload: true,
        downloadType: 'email',
        accountNumber: activeAccountNumber,
      };
      setSubmitStatus(AxiosStatus.PENDING);
      return api
        .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          closeOTPModal();
          closeModal();
        })
        .then(() => api.post('account/transactions/statement', body))
        .then((data: IApiResponse) => {
          setSuccessMsg(data.description ?? 'E-mail Statement has been sent!');
          setSubmitStatus(AxiosStatus.SUCCESS);
        })
        .catch((error: IApiResponse) => {
          setErrorMsg(error.description);
          setSubmitStatus(AxiosStatus.FAILURE);
        })
        .catch((error: IOTPError) => {
          setOTPModalError(error);
          if (error.failureCount === 0 && form) {
            form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
          }
          return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
        });
    },
    [email, startDate, endDate, activeAccountNumber, closeModal],
  );

  return (
    <>
      <ModalWindow open={open} onClose={closeModal} fullScreen>
        <>
          <NavigationTitle to="/" layoutType={LayoutType.dashboard} onClick={closeModal} noArrow={!downMdScreen}>
            E-mail Statement
          </NavigationTitle>
          <Form
            onSubmit={handleEmailSubmit}
            validate={validate}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit} noValidate>
                {!downMdScreen && (
                  <div className={styles.message}>
                    Enter your E-mail address to recieve Transaction History Statement
                  </div>
                )}
                <div className={styles.resendContainer}>
                  <Input name="email" label="E-mail" />
                </div>
                <Button type="submit" theme={ButtonThemes.primary} fullWidth disabled={submitting}>
                  {submitting ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
                </Button>
              </form>
            )}
          />
        </>
      </ModalWindow>
      <OTPVerificationModal
        open={isOTPModalOpen}
        onClose={closeOTPModal}
        onSubmit={doSubmit}
        onResend={onResend}
        OTPModalError={OTPModalError}
      />
      <StatusModal
        open={showStatusModal}
        status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
        onClose={onCloseStatusModal}
        onRetry={onCloseStatusModal}
        onRetryText="Close"
      >
        {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
      </StatusModal>
    </>
  );
};

export default EmailStatementModal;
